import { Datetime, titleCase } from '@/utils';
import { TrackFlightLabelEnum, Trip, TripKindEnum } from '@/models/gen/graphql';

import { Badge } from 'react-bootstrap';
import { ReactNode } from 'react';
import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import Tooltip from '@/components/Tooltip';
import { useSimpleTableRowContext } from '@/components/SimpleTable/SimpleTableRow';

const LATE_THRESHOLD_MINUTES = 1;
const VERY_LATE_THRESHOLD_HOURS = 5;

const getActualTimeVariant = (scheduled: string, actual: Datetime): string => {
  if (!actual || !scheduled) return;
  const actualDatetime = actual.asDayjs();
  if (actualDatetime.isBefore(scheduled)) return 'success';
  const isLate = actualDatetime.diff(scheduled, 'minutes') >= LATE_THRESHOLD_MINUTES;
  const isVeryLate = actualDatetime.diff(scheduled, 'hours') >= VERY_LATE_THRESHOLD_HOURS;
  if (isLate && !isVeryLate) return 'warning-dark';
  if (isVeryLate) return 'danger';
};

const ActualCell = (): ReactNode => {
  const { data: { trackFlight, kind, scheduled, offset } = {} } = useSimpleTableRowContext<Trip>();
  const { actual, arrivalGate, arrivalTerminal, label, lastPingTime } = trackFlight || {};

  const actualDatetime = actual ? new Datetime(actual) : null;
  const variant = getActualTimeVariant(scheduled, actualDatetime);

  // We want this to execute on hover so the from now displays the correct minutes.
  // DO NOT memoize this function.
  const ToolTipDisplay = () => {
    const fromNow = new Datetime(lastPingTime).asDayjs().utc(true).fromNow();
    const lastPingDatetime = new Datetime(lastPingTime).applyOffset(offset);
    return (
      <div className="d-flex align-items-center justify-content-center px-2">
        <div className="pe-3">
          <Badge className="d-flex align-items-center justify-content-center fs-6" bg={variant}>
            {label[0]}
          </Badge>
          {actual && <span className={`text-${variant}`}>{actualDatetime?.time || '--:--'}</span>}
        </div>
        <div>
          <div>
            Status: <strong>{titleCase(label?.replace('_', ' '))}</strong>
          </div>
          {label === TrackFlightLabelEnum.EnRoute && (
            <div>
              ETA: <strong>{actualDatetime?.time || 'Unknown'}</strong>
            </div>
          )}
          {kind === TripKindEnum.Arr && (
            <>
              {arrivalGate && (
                <div>
                  Gate: <strong>{arrivalGate}</strong>
                </div>
              )}
              {arrivalTerminal && (
                <div>
                  Terminal: <strong>{arrivalTerminal}</strong>
                </div>
              )}
            </>
          )}
          {lastPingTime && <i>{`Updated ${lastPingDatetime.time} (${fromNow})`}</i>}
        </div>
      </div>
    );
  };

  return (
    <SimpleTableCell name="ACTUAL" className="flex-column gap-1 w-sm-grow">
      {!actual && !label && '--'}
      {label && (
        <Tooltip content={<ToolTipDisplay />}>
          <Badge className="d-flex align-items-center justify-content-center fs-6" bg={variant}>
            {label[0]}
          </Badge>
        </Tooltip>
      )}
      {actual && <span className={`text-${variant}`}>{actualDatetime.time}</span>}
    </SimpleTableCell>
  );
};

export default ActualCell;
